/*
  Closing the widget only hides it for the current page view - no
  persistence, so a refresh always brings it back rather than staying
  dismissed. Closing also pauses and re-mutes the video: it keeps playing
  in the DOM underneath the hidden widget otherwise, so a shopper who'd
  unmuted it would still hear audio with nothing visible on screen.
*/
class ShoppableVideoWidget extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('.shoppable-video-widget__video');

    const closeButton = this.querySelector('.shoppable-video-widget__close');
    closeButton?.addEventListener('click', () => {
      this.hidden = true;
      if (video) {
        video.pause();
        video.muted = true;
      }
    });

    video?.addEventListener('click', (event) => {
      if (this.dragMoved) {
        // A drag just ended on this pointerup - swallow the click it
        // otherwise fires right after, so dragging doesn't also toggle mute.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      video.muted = !video.muted;
    });

    this.setupDrag();
  }

  /*
    Pointer Events cover mouse drag and touch drag with one code path.
    Position is tracked as left/top px (converted from the initial
    right/bottom CSS placement on first drag) and clamped every move so the
    widget can be dropped anywhere but never overflows the viewport.
  */
  setupDrag() {
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let originLeft = 0;
    let originTop = 0;

    const clamp = (value, max) => Math.min(Math.max(value, 0), Math.max(max, 0));

    const applyPosition = (left, top) => {
      const maxLeft = window.innerWidth - this.offsetWidth;
      const maxTop = window.innerHeight - this.offsetHeight;
      const clampedLeft = clamp(left, maxLeft);
      const clampedTop = clamp(top, maxTop);
      this.style.left = `${clampedLeft}px`;
      this.style.top = `${clampedTop}px`;
      this.style.right = 'auto';
      this.style.bottom = 'auto';
      return { clampedLeft, clampedTop };
    };

    this.addEventListener('pointerdown', (event) => {
      if (event.button != null && event.button !== 0 && event.pointerType === 'mouse') return;
      if (event.target.closest('.shoppable-video-widget__close')) return;
      pointerId = event.pointerId;
      this.dragMoved = false;
      const rect = this.getBoundingClientRect();
      originLeft = rect.left;
      originTop = rect.top;
      startX = event.clientX;
      startY = event.clientY;
      this.setPointerCapture(pointerId);
      this.classList.add('is-dragging');
    });

    this.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!this.dragMoved && Math.hypot(dx, dy) > 4) {
        this.dragMoved = true;
      }
      if (!this.dragMoved) return;
      applyPosition(originLeft + dx, originTop + dy);
    });

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      this.classList.remove('is-dragging');
    };

    this.addEventListener('pointerup', endDrag);
    this.addEventListener('pointercancel', endDrag);

    // Re-clamp on viewport resize (e.g. rotation) so it can't get stranded
    // off-screen if the widget was dropped near an edge that shrinks.
    window.addEventListener('resize', () => {
      if (this.style.left === '' || this.style.right !== 'auto') return;
      const rect = this.getBoundingClientRect();
      applyPosition(rect.left, rect.top);
    });
  }
}

customElements.define('shoppable-video-widget', ShoppableVideoWidget);
